import type { AdminSport, Integration, SportIntegration } from './api'
import { useEffect, useState } from 'react'
import { fetchIntegrations, fetchSportIntegrations, integrationKeyFromLabel, INTEGRATIONS_MIN_ROLE } from './api'
import IntegrationLeagueEditor from './IntegrationLeagueEditor'
import { MarketChip, ReviewNotes, StatusLegend } from './IntegrationStatus'

interface Props {
  sport: Pick<AdminSport, 'id' | 'name' | 'slug'>
  // Write tier (migration 0070). The section itself is rendered only when
  // the session clears INTEGRATIONS_MIN_ROLE — SportEditor gates that.
  canEdit: boolean
}

// Sport-page section: which feed integrations carry this sport and the
// status of each market. Self-fetching (same lifecycle as MarketsTradedPanel).
// Editing is per integration row through the shared league editor with the
// sport locked; + ADD INTEGRATION lists this sport under one more integration.
export default function IntegrationMarketsPanel({ sport, canEdit }: Props) {
  const [rows, setRows] = useState<SportIntegration[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  // The catalog of integrations, needed only for + ADD INTEGRATION.
  const [all, setAll] = useState<Integration[] | null>(null)
  const [addId, setAddId] = useState<number | ''>('')
  const [adding, setAdding] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    setRows(null)
    setError(null)
    setEditingId(null)
    setAdding(false)
    fetchSportIntegrations(sport.id)
      .then((r) => {
        if (!cancelled) setRows(r)
      })
      .catch((e) => {
        if (!cancelled) setError((e as Error).message)
      })
    return () => {
      cancelled = true
    }
  }, [sport.id, tick])

  useEffect(() => {
    if (!canEdit) return
    let cancelled = false
    fetchIntegrations()
      .then((list) => {
        if (!cancelled) setAll(list)
      })
      .catch(() => { /* non-fatal — only disables + ADD INTEGRATION */ })
    return () => {
      cancelled = true
    }
  }, [canEdit, tick])

  function refresh(text: string) {
    setEditingId(null)
    setAdding(false)
    setAddId('')
    setMsg(text)
    setTick(t => t + 1)
  }

  // Key this sport is already filed under (any integration) — reuse it so
  // one sport does not end up under two keys; else derive from the slug.
  const knownKey = rows?.[0]?.league_key
    ?? all?.flatMap(i => i.leagues).find(l => l.sport_id === sport.id)?.league_key
    ?? (sport.slug ? integrationKeyFromLabel(sport.slug) : integrationKeyFromLabel(sport.name))
  const knownName = rows?.[0]?.league_name ?? sport.name

  const addable = (all ?? []).filter(i => !rows?.some(r => r.integration_id === i.id))
  const addTarget = typeof addId === 'number' ? all?.find(i => i.id === addId) ?? null : null

  return (
    <div className="ad-editor-section">
      <div className="ad-section-head">
        <h3 className="b-mono">INTEGRATIONS</h3>
        {INTEGRATIONS_MIN_ROLE === 'owner' && (
          <span className="b-mono-dim b-tiny">OWNER PREVIEW</span>
        )}
      </div>
      <p className="b-mono-dim ad-section-hint">
        Feed integrations that carry this sport and where each market stands.
        {canEdit ? ' Edits save straight to the database and land in the AUDIT LOG.' : ''}
      </p>

      {msg && <p className="ad-msg b-mono is-ok">{msg}</p>}

      {error
        ? <p className="ad-msg b-mono is-err">{error}</p>
        : rows == null
          ? <p className="b-mono-dim b-tiny">Loading…</p>
          : (
              <>
                {rows.length === 0 && !adding && (
                  <p className="b-mono-dim ad-list-empty">no integration rows for this sport</p>
                )}
                {rows.length > 0 && (
                  <div className="ad-int-rows">
                    {rows.map(r => (
                      editingId === r.integration_id
                        ? (
                            <IntegrationLeagueEditor
                              key={`edit-${r.integration_id}`}
                              integrationId={r.integration_id}
                              integrationName={r.integration_name}
                              initial={{
                                league_key: r.league_key,
                                league_name: r.league_name,
                                sport_id: sport.id,
                                markets: r.markets,
                              }}
                              takenKeys={[]}
                              knownLeagues={[]}
                              onSaved={() => refresh(`Saved ${r.integration_name}.`)}
                              onCancel={() => setEditingId(null)}
                            />
                          )
                        : (
                            <div key={r.integration_id} className="ad-int-row">
                              <span className="ad-int-row-name b-mono">
                                {r.integration_name}
                                <span className="b-mono-dim ad-list-slug">
                                  {' '}
                                  ·
                                  {r.league_key}
                                </span>
                              </span>
                              <div>
                                <div className="ad-int-chips">
                                  {r.markets.map(m => <MarketChip key={m.market_key} market={m} />)}
                                  {canEdit && (
                                    <button
                                      type="button"
                                      className="b-btn b-btn-tiny"
                                      onClick={() => { setEditingId(r.integration_id); setAdding(false); setMsg(null) }}
                                      disabled={editingId != null || adding}
                                    >
                                      EDIT
                                    </button>
                                  )}
                                </div>
                                <ReviewNotes markets={r.markets} />
                              </div>
                            </div>
                          )
                    ))}
                  </div>
                )}

                {canEdit && adding && addTarget && (
                  <IntegrationLeagueEditor
                    key={`add-${addTarget.id}`}
                    integrationId={addTarget.id}
                    integrationName={addTarget.name}
                    initial={{ league_key: knownKey, league_name: knownName, sport_id: sport.id, markets: [] }}
                    takenKeys={[]}
                    knownLeagues={[]}
                    onSaved={() => refresh(`${sport.name} added to ${addTarget.name}.`)}
                    onCancel={() => { setAdding(false); setAddId('') }}
                  />
                )}

                {canEdit && !adding && editingId == null && (
                  <div className="ad-int-edit-add">
                    <select
                      value={addId}
                      onChange={e => setAddId(e.target.value === '' ? '' : Number(e.target.value))}
                      className="ad-int-select"
                      disabled={!all || addable.length === 0}
                      aria-label="Integration to add"
                    >
                      <option value="">
                        {!all ? 'loading integrations…' : addable.length === 0 ? 'every integration already lists this sport' : '— pick an integration —'}
                      </option>
                      {addable.map(i => (
                        <option key={i.id} value={i.id}>{i.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="b-btn b-btn-primary b-btn-tiny"
                      onClick={() => { setAdding(true); setMsg(null) }}
                      disabled={typeof addId !== 'number'}
                    >
                      + ADD INTEGRATION
                    </button>
                  </div>
                )}

                {rows.length > 0 && <StatusLegend />}
              </>
            )}
    </div>
  )
}
